/**
 * Coerces a caught API error (typically an Axios error against this app's
 * Strapi backend) into a plain, displayable string.
 *
 * Two shapes reach the frontend for the same endpoint depending on how the
 * backend failed:
 *  1. A controller-handled error: `{ error: 'some_error_code' }` — the
 *     `error` field is already a string.
 *  2. An uncaught throw hits Strapi's global error handler, which responds
 *     with `{ data: null, error: { status, name, message } }` — `error` is
 *     an object and the human-readable text is at `error.message`.
 *
 * Passing either shape's `error` field straight into a React child (e.g.
 * `toast({ description: error.response.data.error })`) works for shape 1 but
 * white-screens the app for shape 2, since React refuses to render a plain
 * object as a child. This helper normalizes both shapes (plus a couple of
 * other common axios/Error shapes) into a string, and is defensive against
 * any further level of nesting that turns out to be a non-string object —
 * it NEVER returns anything but a string.
 *
 * Precedence:
 *   1. `error.response.data.error` when it is already a string
 *   2. `error.response.data.error.message` when that nested value is a string
 *   3. `error.response.data.message`
 *   4. `error.message` (covers plain `Error` instances too)
 *   5. `fallback` (defaults to `''` when omitted)
 */
export function resolveApiErrorMessage(error: unknown, fallback = ''): string {
  const data = getResponseData(error);

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;

    if (typeof record.error === 'string') {
      return record.error;
    }

    if (record.error && typeof record.error === 'object') {
      const nestedMessage = (record.error as Record<string, unknown>).message;
      if (typeof nestedMessage === 'string') {
        return nestedMessage;
      }
    }

    if (typeof record.message === 'string') {
      return record.message;
    }
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }

  return fallback;
}

function getResponseData(error: unknown): unknown {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== 'object') {
    return undefined;
  }
  return (response as { data?: unknown }).data;
}
